import SqFetch from 'application/fetch';
import { createVerificationDetails } from './web_payment_sdk';
import { CARD_INTENT } from 'constants/enums';
import { trackCdp } from 'utilities/tracking';

const createPaymentData = (tokenResult) => {
  if (tokenResult.details?.cashAppPay?.cashtag) {
    return { cashtag: tokenResult.details.cashAppPay.cashtag };
  } else {
    const cardData = tokenResult.details.card;
    const postalCode = tokenResult.details.billing?.postalCode;
    return {
      card_data: {
        card_brand: cardData.brand,
        last_4: cardData.last4,
        exp_year: cardData.expYear,
        exp_month: cardData.expMonth,
        // Backend reads credit_card_info[:billing_postal_code]; must match.
        billing_postal_code: postalCode,
      },
    };
  }
};

const getCardIntent = (orderData, saveCardToWallet) => {
  if (saveCardToWallet && !orderData?.is_hardware_rental_order) {
    return CARD_INTENT.CHARGE_AND_STORE;
  }
  return orderData.card_intent?.intent?.toUpperCase() || CARD_INTENT.CHARGE;
};

const createShopPaymentWithSavedCard = (
  orderNumber,
  orderData,
  selectedCard,
) => {
  const requestParams = {
    order_number: orderNumber,
    wallet_payment_source_id: selectedCard.id,
    forter_token: window.ftrToken,
    save_to_wallet: false,
    card_data: {
      card_brand: selectedCard.payment_source.brand,
      last_4: selectedCard.payment_source.last_digits,
      exp_year: selectedCard.payment_source.year,
      exp_month: selectedCard.payment_source.month,
      billing_postal_code:
        selectedCard.payment_source?.address?.postal_code || '',
    },
  };

  return SqFetch.post('../../api/v1/payments', requestParams);
};

// Mirrors paymentMethodFromTokenResult in shop_payments.ts — keep in sync.
const paymentMethodFromTokenResult = (tokenResult) =>
  (tokenResult.details?.method || 'unknown')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase();

const createShopPaymentWithToken = async (
  orderNumber,
  orderData,
  tokenResult,
  sqPayments,
  issuerInstallmentsTerm = null,
  saveCardToWallet = false,
) => {
  const cardIntent = getCardIntent(orderData, saveCardToWallet);
  const paymentMethod = paymentMethodFromTokenResult(tokenResult);

  let scaResponse;
  try {
    scaResponse = await sqPayments.verifyBuyer(
      tokenResult.token,
      createVerificationDetails(orderData, cardIntent),
    );
  } catch (e) {
    // Same event shape as checkout_v3 (shop_payments.ts).
    trackCdp({
      name: 'verifyBuyer error',
      params: {
        feature: 'shop',
        surface: 'checkout_legacy',
        order_number: orderNumber,
        save_card_to_wallet: saveCardToWallet,
        card_intent: cardIntent,
        payment_method: paymentMethod,
        error_message: e.message,
        error_name: e.name,
        error_type: e?.errors?.[0]?.type,
        error_code: e?.errors?.[0]?.code,
        error_stack: e.stack,
      },
    });
    throw e;
  }

  // The raw verification token is a payment credential — deliberately not logged.
  trackCdp({
    name: 'verifyBuyer response',
    params: {
      feature: 'shop',
      surface: 'checkout_legacy',
      order_number: orderNumber,
      user_challenged: scaResponse?.userChallenged ?? false,
      save_card_to_wallet: saveCardToWallet,
      card_intent: cardIntent,
      payment_method: paymentMethod,
      has_verification_token: Boolean(scaResponse?.token),
    },
  });

  if (!scaResponse) {
    // verifyBuyer's return type includes null; tracked above, fail the submit here.
    throw new TypeError('verifyBuyer resolved without a response');
  }

  const requestParams = {
    order_number: orderNumber,
    nonce: tokenResult.token,
    verification_token: scaResponse.token, // buyer verification token
    forter_token: window.ftrToken,
    issuer_number_of_installments: issuerInstallmentsTerm,
    save_to_wallet: saveCardToWallet,
    wallet_payment_source_id: null,
    ...createPaymentData(tokenResult),
  };

  return SqFetch.post('../../api/v1/payments', requestParams);
};

const checkPreAuthFraud = (orderNumber, tokenResult) => {
  try {
    const requestParams = {
      order_number: orderNumber,
      nonce: tokenResult.token,
      forter_token: window.ftrToken,
      ...createPaymentData(tokenResult),
    };
    return SqFetch.post('../../api/v1/pre-auth', requestParams);
  } catch {
    return null;
  }
};

export {
  createShopPaymentWithToken,
  createShopPaymentWithSavedCard,
  checkPreAuthFraud,
};
